import { useEffect, useState } from 'react'
import { diffLines } from 'diff'
import { DiffResult } from '../types'

export const useDiff = (oldText: string, newText: string): DiffResult[] => {
  const [diff, setDiff] = useState<DiffResult[]>([])
  
  useEffect(() => {
    const differences = diffLines(oldText, newText)
    setDiff(differences)
  }, [oldText, newText])
  
  return diff
}

export const useDebounce = <T,>(value: T, delay: number = 500): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    
    return () => clearTimeout(timer)
  }, [value, delay])
  
  return debouncedValue
}

export const useLocalStorage = <T,>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.error(error)
      return initialValue
    }
  })
  
  const setValue = (value: T | ((prev: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      window.localStorage.setItem(key, JSON.stringify(valueToStore))
    } catch (error) {
      console.error(error)
    }
  }
  
  return [storedValue, setValue]
}

export const useCopyToClipboard = () => {
  const [copied, setCopied] = useState(false)
  
  const copy = async (text: string) => {
    if (!navigator.clipboard) {
      return false
    }
    
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      return true
    } catch (error) {
      setCopied(false)
      return false
    }
  }
  
  return { copy, copied }
}

export const useWordCount = (text: string) => {
  const [count, setCount] = useState(0)
  
  useEffect(() => {
    const words = text.trim().split(/\s+/).filter(w => w.length > 0)
    setCount(words.length)
  }, [text])
  
  return count
}
